import EventEmitter from 'events';
import { useAuth } from 'react-oidc-context';
import * as Sentry from '@sentry/react';
import {
  Basket,
  BasketItem,
  BasketMeta,
  BasketQuote,
  BasketSection,
  CountryResponse,
  GenericErrorResponse,
  OrderValidation,
  QuoteAddress,
  QuoteCountry,
  QuoteParcels,
  QuoteProtection,
  QuoteRequirements,
  QuoteResponse
} from '~/lib/types/basket-types';
import { useTenant, useSocket, useAnalytics } from '@parcel2go/npm-p2g-app';
import { authenticatedFetchWithTenantAnalytics, fetchWithTenantAnalytics } from '~/lib/fetch';
import { createContext, useEffect, useReducer, useRef, useState } from 'react';
import { DEBOUNCE_TIME, QUOTE_API } from '~/lib/constants';
import { BasketSections } from '~/lib/types/enums';
import {
  getBasketItemQuotes,
  getCountryRedirect,
  loadBasket,
  notifyBasketUpdate,
  storeBasketQuote
} from '~/lib/basket';
import { onlyUnique } from '~/lib/utils/array';
import { getBasketAction, getCurrentItemId } from '~/lib/getCurrentItemId';
import { quoteToProtectionOptions } from '~/lib/quotes';
import { captureException, captureWarning, publishEvent } from '~/lib/metrics';
import { useNavigate } from 'react-router-dom';
import Loading from '~/components/core/progress/loading';
import { useRequirementsApi } from '~/hooks/use-requirements-api';

export interface FlatBasket {
  id: string;
  items: { [key: string]: { [key: string]: unknown } };
}

declare global {
  interface Window {
    basket: FlatBasket;
  }
}

export interface IBasketContext {
  basket: Basket;
  fetching: boolean;
  saving: boolean;
  validating: boolean;
  savingItem: boolean;
  initialised: boolean;
  error: string | null;
  events: EventEmitter;
  broadcast: BroadcastChannel;
  currentItem: string;
  currentAction?: string | null;
  requote: boolean;
  protection: QuoteProtection;
  requirements: QuoteRequirements;
  origins: QuoteCountry[];
  destinations: QuoteCountry[];
  changes: string[];
  validationErrors: GenericErrorResponse | null;
  validationWarnings: GenericErrorResponse[];
  order: OrderValidation | null;
}

export interface IResetBasketAction {
  type: 'RESET';
}

export interface IInitialiseBasketAction {
  type: 'INIT_BASKET';
  basket: Basket;
  currentItem?: string;
}

export interface IAddBasketItemAction {
  type: 'ADD_ITEM';
  id: string;
  item: BasketItem;
}

export interface IUpdateBasketItemAction {
  type: 'UPDATE_ITEM';
  id: string;
  item: BasketItem;
}

export interface IRemoveBasketItemAction {
  type: 'REMOVE_ITEM';
  id: string;
}

export interface IUpdateBasketSectionAction {
  type: 'UPDATE_SECTION';
  id: string;
  name: string;
  section: BasketSection;
}

export interface IUpdateBasketSectionsAction {
  type: 'UPDATE_SECTIONS';
  id: string;
  sections: { [key: string]: BasketSection };
}

export interface IUpdateBasketSectionPayloadAction {
  type: 'UPDATE_SECTION_PAYLOAD';
  id: string;
  name: string;
  payload: unknown;
}

export interface IUpdateBasketSectionsPayloadAction {
  type: 'UPDATE_SECTIONS_PAYLOAD';
  id: string;
  sections: { [name: string]: unknown };
}

export interface IUpdateBasketLoadingAction {
  type: 'UPDATE_LOADING';
  fetching: boolean;
  saving: boolean;
}

export interface ISavingItemAction {
  type: 'SAVING_ITEM';
  saving: boolean;
}

export interface IChangeCurrentItemAction {
  type: 'CHANGE_ITEM';
  item: string;
  action?: string | null;
}

export interface IRequestRequoteAction {
  type: 'REQUEST_REQUOTE';
  value: boolean;
}

export interface IUpdateQuoteRequirementsAction {
  type: 'UPDATE_REQUIREMENTS';
  requirements: QuoteRequirements;
}

export interface IUpdateQuoteProtectionAction {
  type: 'UPDATE_PROTECTION';
  protection: QuoteProtection;
}

export interface IUpdateQuoteOriginsAction {
  type: 'UPDATE_ORIGINS';
  options: QuoteCountry[];
}

export interface IUpdateQuoteDestinationsAction {
  type: 'UPDATE_DESTINATIONS';
  options: QuoteCountry[];
}

export interface IUpdateLoadingProtectionAction {
  type: 'LOADING_PROTECTION';
  loading?: boolean;
}

export interface IClearActionsAction {
  type: 'UPDATE_CHANGES';
  changes: string[];
}

export interface IAddChangesAction {
  type: 'ADD_CHANGES';
  changes: string[];
}

export interface IUpdateValidationError {
  type: 'UPDATE_ORDER_VALIDATION_ERRORS';
  errors: GenericErrorResponse;
}

export interface IUpdateValidationLoading {
  type: 'UPDATE_ORDER_VALIDATING';
  validating: boolean;
}

export interface IUpdateValidation {
  type: 'UPDATE_ORDER_VALIDATION';
  breakdown: OrderValidation;
  warnings: GenericErrorResponse[];
}

export type BasketContextActions =
  | IResetBasketAction
  | IUpdateQuoteOriginsAction
  | IUpdateQuoteDestinationsAction
  | IUpdateQuoteRequirementsAction
  | IUpdateBasketSectionAction
  | IUpdateBasketSectionsAction
  | IAddBasketItemAction
  | IInitialiseBasketAction
  | IUpdateBasketSectionPayloadAction
  | IUpdateBasketSectionsPayloadAction
  | IUpdateBasketLoadingAction
  | IChangeCurrentItemAction
  | IUpdateBasketItemAction
  | IRemoveBasketItemAction
  | IRequestRequoteAction
  | IClearActionsAction
  | IUpdateQuoteProtectionAction
  | IUpdateLoadingProtectionAction
  | ISavingItemAction
  | IUpdateValidationError
  | IUpdateValidationLoading
  | IAddChangesAction
  | IUpdateValidation;

const initialState: IBasketContext = {
  basket: {
    id: '',
    items: {}
  },
  changes: [],
  fetching: true,
  initialised: false,
  saving: false,
  savingItem: false,
  error: null,
  events: new EventEmitter(),
  currentItem: '',
  requote: false,
  broadcast: new BroadcastChannel('parcel2go-basket'),
  origins: [],
  destinations: [],
  requirements: {},
  protection: { loading: false, options: [] },
  validating: false,
  validationErrors: null,
  validationWarnings: [],
  order: null
};

type BasketContextState = {
  state: IBasketContext;
  dispatch: (action: BasketContextActions) => void;
};

const BasketContext = createContext<BasketContextState>({
  state: initialState,
  dispatch: () => {}
});

const reducer = (state: IBasketContext, action: BasketContextActions): IBasketContext => {
  switch (action.type) {
    case 'RESET':
      return initialState;
    case 'INIT_BASKET': {
      const idToTry = getCurrentItemId() || '';
      const currentAction = getBasketAction();
      const hasItem = !!action.basket?.items[idToTry];
      return {
        ...state,
        initialised: true,
        fetching: false,
        saving: false,
        error: null,
        basket: action.basket,
        currentItem: hasItem ? idToTry : '',
        currentAction: currentAction
      };
    }
    case 'ADD_ITEM':
      return {
        ...state,
        changes: [],
        basket: { ...state.basket, items: { ...state.basket.items, [action.id]: { sections: action.item.sections } } }
      } as IBasketContext;
    case 'UPDATE_SECTION':
      return {
        ...state,
        basket: {
          ...state.basket,
          items: {
            ...state.basket.items,
            [action.id]: { sections: { ...state.basket.items[action.id]?.sections, [action.name]: action.section } }
          }
        }
      } as IBasketContext;
    case 'UPDATE_SECTIONS':
      return {
        ...state,
        basket: {
          ...state.basket,
          items: {
            ...state.basket.items,
            [action.id]: { sections: { ...state.basket.items[action.id]?.sections, ...action.sections } }
          }
        }
      } as IBasketContext;
    case 'UPDATE_SECTION_PAYLOAD':
      return {
        ...state,
        basket: {
          ...state.basket,
          items: {
            ...state.basket.items,
            [action.id]: {
              sections: {
                ...state.basket.items[action.id].sections,
                [action.name]: { ...state.basket.items[action.id].sections[action.name], payload: action.payload }
              }
            }
          }
        }
      } as IBasketContext;
    case 'UPDATE_SECTIONS_PAYLOAD': {
      return {
        ...state,
        changes: [...state.changes, ...Object.keys(action.sections)].filter(onlyUnique),
        basket: {
          ...state.basket,
          items: {
            ...state.basket.items,
            [action.id]: {
              ...state.basket.items[action.id],
              sections: {
                ...state.basket.items[action.id].sections,
                ...Object.entries(action.sections).reduce(
                  (acc, [key, payload]) => ({
                    ...acc,
                    [key]: {
                      ...state.basket.items[action.id].sections[key],
                      payload
                    }
                  }),
                  {}
                )
              }
            }
          }
        }
      } as IBasketContext;
    }

    case 'UPDATE_LOADING':
      return { ...state, fetching: action.fetching, saving: action.saving };
    case 'SAVING_ITEM':
      return { ...state, savingItem: action.saving };
    case 'CHANGE_ITEM':
      return { ...state, currentItem: action.item, currentAction: action.action };
    case 'UPDATE_ITEM':
      return { ...state, basket: { ...state.basket, items: { ...state.basket.items, [action.id]: action.item } } };
    case 'REQUEST_REQUOTE':
      return { ...state, requote: action.value };
    case 'REMOVE_ITEM':
      delete state.basket.items[action.id];
      return { ...state, changes: [], basket: { ...state.basket, items: { ...state.basket.items } } };
    case 'UPDATE_REQUIREMENTS':
      return { ...state, requirements: action.requirements };
    case 'UPDATE_CHANGES':
      return { ...state, changes: action.changes.filter(onlyUnique) };
    case 'ADD_CHANGES':
      return { ...state, changes: [...state.changes, ...action.changes].filter(onlyUnique) };
    case 'UPDATE_ORIGINS':
      return { ...state, origins: action.options };
    case 'UPDATE_DESTINATIONS':
      return { ...state, destinations: action.options };
    case 'UPDATE_PROTECTION':
      return { ...state, protection: action.protection };
    case 'LOADING_PROTECTION':
      return {
        ...state,
        protection: {
          ...state.protection,
          loading: action.loading === undefined ? true : action.loading,
          requestId: undefined
        }
      };
    case 'UPDATE_ORDER_VALIDATION_ERRORS':
      return { ...state, validationErrors: action.errors, order: null, validationWarnings: [], validating: false };
    case 'UPDATE_ORDER_VALIDATING':
      return { ...state, validating: action.validating };
    case 'UPDATE_ORDER_VALIDATION':
      return {
        ...state,
        order: action.breakdown,
        validationErrors: null,
        validationWarnings: action.warnings,
        validating: false
      };
  }
  return state;
};

type BasketProviderProps = {
  children: React.ReactNode;
  events: EventEmitter;
};

const BasketProvider: React.FC<BasketProviderProps> = (props) => {
  const initialised = useRef(false);
  const { tenant, settings } = useTenant();
  const { isLoading, isAuthenticated, user } = useAuth();
  const { session } = useAnalytics();
  const [state, dispatch] = useReducer(reducer, initialState);
  useSocket({ sessionId: state.basket.id, ready: state.initialised });
  const navigate = useNavigate();
  const { fetchRequirements } = useRequirementsApi();

  const [requirementsRequest, setRequirementsRequest] = useState<{
    tenant: string;
    settings: { [key: string]: string };
    origin: string;
    destination: string;
    service: string;
  }>();

  const [protectionRequest, setProtectionRequest] = useState<{
    tenant: string;
    settings: { [key: string]: string };
    service: string;
    origin: string;
    originPostcode: string | undefined | null;
    destination: string;
    destinationPostcode: string | undefined | null;
    parcels: { weight: number; value: number; quantity: number }[];
  }>();

  const value = { state, dispatch };

  async function init() {
    const { basket, id, itemId } = await loadBasket(tenant, settings, session, user);
    if (!basket) {
      dispatch({ type: 'INIT_BASKET', basket: { id: id, items: {} } });
      return;
    }

    if (itemId && !basket?.items[itemId]) {
      captureWarning(
        'Item not found',
        {
          section: 'basket'
        },
        [itemId]
      );
    }

    dispatch({
      type: 'INIT_BASKET',
      basket: basket,
      currentItem: itemId && basket.items[itemId] ? itemId : Object.keys(basket.items)[0]
    });
  }

  //Keep UI up to date between tabs using the Broadcast API.
  useEffect(() => {
    async function onMessage(event: MessageEvent) {
      console.debug('Message received', event.data);
      if (event.data.type === 'basket_section_saved') {
        dispatch({ type: 'UPDATE_SECTION', id: event.data.id, name: event.data.section, section: event.data.payload });
        if (state.currentItem === event.data.id) {
          navigate('/order-process/confirm', { replace: true });
        }
      } else if (event.data.type === 'basket_item_saved' || event.data.type === 'basket_item_fetched') {
        dispatch({
          type: 'UPDATE_ITEM',
          id: event.data.id,
          item: event.data.item
        });
        if (state.currentItem === event.data.id) {
          navigate('/order-process/confirm', { replace: true });
        }
      } else if (event.data.type === 'basket_item_added') {
        dispatch({
          type: 'ADD_ITEM',
          id: event.data.id,
          item: event.data.item
        });
      } else if (event.data.type === 'basket_item_removed') {
        dispatch({ type: 'REMOVE_ITEM', id: event.data.id });
        if (state.currentItem === event.data.id) {
          navigate('/order-process/confirm', { replace: true });
        }
      }
    }

    state.broadcast.addEventListener('message', onMessage);

    return () => {
      state.broadcast.removeEventListener('message', onMessage);
    };
  }, [state.broadcast, dispatch, state.currentItem, navigate]);

  // Reset the basket whenever the tenant changes
  useEffect(() => {
    if (state.initialised) {
      console.debug('TENANT SWITCH', tenant);
      init();
    }
  }, [tenant]);

  //Request a quote when the basket is updated.
  useEffect(() => {
    async function quote() {
      try {
        const result = await getBasketItemQuotes(tenant, settings, state.basket.id, state.currentItem, session, user);

        const previousQuote = state.basket.items[state.currentItem].sections[BasketSections.QUOTE]
          .payload as BasketQuote;

        // The requote returned no matching services, this means the selected service is no longer available of this basket.
        // We need to mark the quote as invalid and update the meta section to reflect this, in turn showing the service switcher.
        if (result.errors?.length || result.quotes.length === 0) {
          // hmmm what to do here when the quote is invalid? Or should we just remove the item? Or should we just show a message?

          publishEvent('Basket service no longer available', {
            section: 'basket'
          });

          const payload: BasketQuote = {
            ...previousQuote,
            errors: result.errors
          };

          dispatch({
            type: 'UPDATE_SECTION_PAYLOAD',
            id: state.currentItem,
            name: BasketSections.QUOTE,
            payload: payload
          });

          const updatedBasket = await storeBasketQuote(
            tenant,
            settings,
            user,
            session,
            state.basket.id,
            state.currentItem,
            {
              ...state.basket.items[state.currentItem].sections[BasketSections.QUOTE],
              payload: payload
            }
          );

          notifyBasketUpdate(state.currentItem, dispatch, state.broadcast, state.events, updatedBasket);

          return;
        }

        const quote: BasketQuote = result.quotes[0];

        if (result.etag && result.etag === previousQuote.etag) {
          return;
        }

        dispatch({
          type: 'UPDATE_SECTION_PAYLOAD',
          id: state.currentItem,
          name: BasketSections.QUOTE,
          payload: quote
        });

        dispatch({ type: 'UPDATE_PROTECTION', protection: quoteToProtectionOptions(quote) });

        const payload: BasketQuote = { ...quote, etag: result.etag, errors: undefined };

        // Update the quote on the basket
        const updatedBasket = await storeBasketQuote(
          tenant,
          settings,
          user,
          session,
          state.basket.id,
          state.currentItem,
          {
            ...state.basket.items[state.currentItem].sections[BasketSections.QUOTE],
            payload: payload
          }
        );

        notifyBasketUpdate(state.currentItem, dispatch, state.broadcast, state.events, updatedBasket);
      } catch (e) {
        captureException(e, 'Failed to request quote', {
          section: 'basket'
        });
      } finally {
        dispatch({
          type: 'LOADING_PROTECTION',
          loading: false
        });
      }
    }

    if (state.requote) {
      dispatch({ type: 'REQUEST_REQUOTE', value: false });
      dispatch({ type: 'LOADING_PROTECTION', loading: true });
      quote();
    }
  }, [
    isAuthenticated,
    session,
    settings,
    state.basket.id,
    state.basket.items,
    state.broadcast,
    state.currentItem,
    state.events,
    state.requote,
    tenant,
    user
  ]);

  //Initialise the basket when the application starts.
  useEffect(() => {
    if (!state.initialised && !isLoading && !initialised.current) {
      console.debug('INIT');
      initialised.current = true;
      init();
    }
  }, [state.initialised, isLoading]);

  //Build a request from the basket used for gathering basket requirements.
  useEffect(() => {
    const quote = state.basket.items[state.currentItem]?.sections[BasketSections.QUOTE]?.payload as BasketQuote;

    if (!quote) return;

    const origin = state.basket.items[state.currentItem]?.sections[BasketSections.ORIGIN]?.payload as QuoteAddress;

    if (!origin) return;

    const destination = state.basket.items[state.currentItem]?.sections[BasketSections.DESTINATION]
      ?.payload as QuoteAddress;

    if (!destination) return;

    if (
      requirementsRequest?.tenant !== tenant ||
      requirementsRequest?.service !== quote?.service?.slug ||
      origin?.country !== requirementsRequest?.origin ||
      destination?.country !== requirementsRequest?.destination
    ) {
      setRequirementsRequest({
        tenant: tenant,
        settings: settings,
        origin: origin.country,
        destination: destination.country,
        service: quote.service.slug
      });
    }
  }, [state.basket.items, state.currentItem, requirementsRequest, tenant, settings]);

  //Manage Country switching
  useEffect(() => {
    const quote = state.basket.items[state.currentItem]?.sections[BasketSections.QUOTE]?.payload as BasketQuote;
    if (!quote) return;

    const origin = state.basket.items[state.currentItem]?.sections[BasketSections.ORIGIN]?.payload as QuoteAddress;
    if (origin) {
      const originRedirect = getCountryRedirect(origin, state.origins);
      if (originRedirect && originRedirect.iso3Code !== origin.country) {
        dispatch({ type: 'UPDATE_CHANGES', changes: [...state.changes, BasketSections.ORIGIN] });
        dispatch({
          type: 'UPDATE_SECTION_PAYLOAD',
          id: state.currentItem,
          name: BasketSections.ORIGIN,
          payload: { ...origin, country: originRedirect.iso3Code }
        });
      }
    }

    const destination = state.basket.items[state.currentItem]?.sections[BasketSections.DESTINATION]
      ?.payload as QuoteAddress;
    if (destination) {
      const destinationRedirect = getCountryRedirect(destination, state.destinations);
      if (destinationRedirect && destinationRedirect.iso3Code !== destination.country) {
        dispatch({ type: 'UPDATE_CHANGES', changes: [...state.changes, BasketSections.DESTINATION] });
        dispatch({
          type: 'UPDATE_SECTION_PAYLOAD',
          id: state.currentItem,
          name: BasketSections.DESTINATION,
          payload: { ...destination, country: destinationRedirect.iso3Code }
        });
      }
    }
  }, [state.basket.items, state.currentItem, state.origins, state.destinations, state.changes]);

  //Build a request from the basket used for gathering protection options.
  useEffect(() => {
    function update() {
      const quote = state.basket.items[state.currentItem]?.sections[BasketSections.QUOTE]?.payload as BasketQuote;

      if (!quote) return;

      const origin = state.basket.items[state.currentItem]?.sections[BasketSections.ORIGIN]?.payload as QuoteAddress;

      if (!origin) return;

      const destination = state.basket.items[state.currentItem]?.sections[BasketSections.DESTINATION]
        ?.payload as QuoteAddress;

      if (!destination) return;

      const parcels = state.basket.items[state.currentItem]?.sections[BasketSections.PARCELS]?.payload as QuoteParcels;

      if (!parcels) return;

      const p = parcels.parcels.map((parcel) => {
        return { weight: parcel.weight || 0, value: parcel.value || 0, quantity: parcel.quantity || 1 };
      });

      const newTotalValue = p.reduce((acc, parcel) => acc + parcel.value * parcel.quantity, 0);
      const newTotalWeight = p.reduce((acc, parcel) => acc + parcel.weight * parcel.quantity, 0);
      if (newTotalValue === 0 || newTotalWeight === 0) {
        dispatch({ type: 'UPDATE_PROTECTION', protection: quoteToProtectionOptions(quote) });
        return;
      }

      const previousTotalWeight = protectionRequest?.parcels.reduce(
        (acc, parcel) => acc + parcel.weight * parcel.quantity,
        0
      );
      const previousTotalValue = protectionRequest?.parcels.reduce(
        (acc, parcel) => acc + parcel.value * parcel.quantity,
        0
      );

      if (
        protectionRequest?.tenant !== tenant ||
        protectionRequest?.service !== quote?.service?.slug ||
        origin?.country !== protectionRequest?.origin ||
        destination?.country !== protectionRequest?.destination ||
        newTotalWeight !== previousTotalWeight ||
        newTotalValue !== previousTotalValue
      ) {
        setProtectionRequest({
          tenant: tenant,
          settings: settings,
          origin: origin.country,
          originPostcode: origin.postcode,
          destination: destination.country,
          destinationPostcode: destination.postcode,
          service: quote.service.slug,
          parcels: p
        });
      }
    }

    const t = setTimeout(update, DEBOUNCE_TIME);
    return () => clearTimeout(t);
  }, [state.basket.items, state.currentItem, protectionRequest, tenant, settings]);

  //Fetch requirements from the quoting API when the request changes.
  useEffect(() => {
    async function requirements() {
      if (requirementsRequest) {
        const result = await fetchRequirements(
          requirementsRequest.service,
          requirementsRequest.origin,
          requirementsRequest.destination
        );

        if (result.data) {
          dispatch({ type: 'UPDATE_REQUIREMENTS', requirements: result.data });
        }
      }
    }

    async function origin() {
      if (requirementsRequest) {
        try {
          const response = await fetchWithTenantAnalytics(
            requirementsRequest.tenant,
            requirementsRequest.settings,
            session,
            `${QUOTE_API}/countries/origins?destinationCountry=${requirementsRequest.destination}&serviceSlug=${requirementsRequest.service}`
          );

          if (response.status === 400) {
            captureWarning('Origins Invalid', {
              section: 'basket'
            });
            return;
          }

          if (!response.ok) {
            throw new Error(`Failed to fetch origins invalid http response ${response.status}`);
          }

          const result = (await response.json()) as CountryResponse;
          if (!result?.success || !result?.result?.countries) {
            throw new Error(`Failed to fetch origins invalid api response ${result.error?.message}`);
          }

          dispatch({ type: 'UPDATE_ORIGINS', options: result.result.countries });
        } catch (e) {
          captureException(e, 'Failed to fetch origins', {
            section: 'basket'
          });
        }
      }
    }

    async function destination() {
      if (requirementsRequest) {
        try {
          const response = await fetchWithTenantAnalytics(
            requirementsRequest.tenant,
            requirementsRequest.settings,
            session,
            `${QUOTE_API}/countries/destinations?originCountry=${requirementsRequest.origin}&serviceSlug=${requirementsRequest.service}`
          );

          if (response.status === 400) {
            captureWarning('Destinations Invalid', {
              section: 'basket'
            });
            return;
          }

          if (!response.ok) {
            throw new Error(`Failed to fetch destinations invalid http response ${response.status}`);
          }

          const result = (await response.json()) as CountryResponse;
          if (!result?.success || !result?.result?.countries) {
            throw new Error(`Failed to fetch destinations invalid api response ${result.error?.message}`);
          }

          dispatch({ type: 'UPDATE_DESTINATIONS', options: result.result.countries });
        } catch (e) {
          captureException(e, 'Failed to fetch destinations', {
            section: 'basket'
          });
        }
      }
    }

    requirements();
    origin();
    destination();
  }, [requirementsRequest, session, fetchRequirements]);

  //Fetch protection from the quoting API when the request changes.
  useEffect(() => {
    async function protection() {
      if (protectionRequest) {
        try {
          dispatch({
            type: 'LOADING_PROTECTION'
          });

          const qs = protectionRequest.parcels
            .map((parcel) => `Quantity=${parcel.quantity}&Weight=${parcel.weight}&ValueInUnits=${parcel.value}`)
            .join('&');

          const response = await authenticatedFetchWithTenantAnalytics(
            user,
            protectionRequest.tenant,
            protectionRequest.settings,
            session,
            `${QUOTE_API}/quote/shipments/${protectionRequest.origin}/${protectionRequest.destination}?ServiceSlugs=${protectionRequest.service}&OriginPostcode=${encodeURIComponent(protectionRequest.originPostcode || '')}&DestinationPostcode=${encodeURIComponent(protectionRequest.destinationPostcode || '')}&${qs}`
          );

          if (response.status === 400) {
            return;
          }

          if (!response.ok) {
            throw new Error(`Failed to fetch protection invalid http response ${response.status}`);
          }

          const result = (await response.json()) as QuoteResponse;
          if (!result?.success || !result?.result?.length) {
            throw new Error(`Failed to fetch protection invalid api response ${result.error?.message}`);
          }

          dispatch({
            type: 'UPDATE_PROTECTION',
            protection: quoteToProtectionOptions(result.result[0])
          });
        } catch (e) {
          captureException(e, 'Failed to fetch protection', {
            section: 'basket'
          });
          dispatch({
            type: 'LOADING_PROTECTION',
            loading: false
          });
        }
      }
    }

    protection();
  }, [protectionRequest, session, user]);

  useEffect(() => {
    if (window) {
      const b: FlatBasket = { id: state.basket.id, items: {} };
      for (const item in state.basket.items) {
        b.items[item] = {};
        for (const section in state.basket.items[item].sections) {
          b.items[item][section] = state.basket.items[item].sections[section].payload;
        }
      }
      window.basket = b;
      Sentry.setContext('basket', { ...b });
    }
  }, [state.basket]);

  useEffect(() => {
    if (!state.initialised || !state.basket.items) {
      props.events.emit('basket_count_updated', 0);
      return;
    }

    const count = Object.entries(state.basket.items).filter(([, item]) => {
      return (item.sections[BasketSections.META]?.payload as BasketMeta)?.completed === true;
    }).length;

    props.events.emit('basket_count_updated', count);
  }, [state.initialised, state.basket.items, props.events]);

  return (
    <BasketContext.Provider value={value}>{!state.initialised ? <Loading /> : props.children}</BasketContext.Provider>
  );
};

const BasketConsumer = BasketContext.Consumer;

export { BasketContext, BasketProvider, BasketConsumer };
